import React, { useState, useEffect } from 'react';
import { firebaseAppointmentService, DateSpecificAvailability } from '../services/firebase/FirebaseAppointmentService';

interface DateSpecificAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerId: string;
  selectedDate: Date;
  onSave: () => void;
}

const DateSpecificAvailabilityModal: React.FC<DateSpecificAvailabilityModalProps> = ({
  isOpen,
  onClose,
  providerId,
  selectedDate,
  onSave
}) => {
  const [isAvailable, setIsAvailable] = useState(true);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [reason, setReason] = useState('');
  const [modalities, setModalities] = useState<('video' | 'in-person')[]>(['video', 'in-person']);
  const [slotDuration, setSlotDuration] = useState(60);
  const [allowedAppointmentTypes, setAllowedAppointmentTypes] = useState<('check-in' | 'therapy' | 'initial-consultation')[]>(['check-in', 'therapy', 'initial-consultation']);

  useEffect(() => {
    if (isOpen) {
      // Load existing override if it exists
      const loadOverride = async () => {
        const existing = await firebaseAppointmentService.getDateSpecificAvailability(providerId, selectedDate);
        if (existing) {
          setIsAvailable(existing.isAvailable);
          setStartTime(existing.startTime || '09:00');
          setEndTime(existing.endTime || '17:00');
          setReason(existing.reason || '');
          setModalities(existing.modalities || ['video', 'in-person']);
          setSlotDuration(existing.slotDuration || 60);
          setAllowedAppointmentTypes((existing as any).allowedAppointmentTypes || ['check-in', 'therapy', 'initial-consultation']);
        } else {
          // Reset to defaults
          setIsAvailable(true);
          setStartTime('09:00');
          setEndTime('17:00');
          setReason('');
          setModalities(['video', 'in-person']);
          setSlotDuration(60);
          setAllowedAppointmentTypes(['check-in', 'therapy', 'initial-consultation']);
        }
      };
      loadOverride();
    }
  }, [isOpen, providerId, selectedDate]);

  const handleSave = async () => {
    const dateStr = selectedDate.toISOString().split('T')[0];

    await firebaseAppointmentService.setDateSpecificAvailability({
      providerId,
      date: dateStr,
      isAvailable,
      startTime: isAvailable ? startTime : undefined,
      endTime: isAvailable ? endTime : undefined,
      modalities: isAvailable ? modalities : undefined,
      slotDuration: isAvailable ? slotDuration : undefined,
      allowedAppointmentTypes: isAvailable ? allowedAppointmentTypes : undefined,
      reason
    } as any);

    onSave();
    onClose();
  };

  const handleDelete = async () => {
    await firebaseAppointmentService.deleteDateSpecificAvailability(providerId, selectedDate);
    onSave();
    onClose();
  };

  const toggleModality = (modality: 'video' | 'in-person') => {
    if (modalities.includes(modality)) {
      setModalities(modalities.filter(m => m !== modality));
    } else {
      setModalities([...modalities, modality]);
    }
  };

  const toggleAppointmentType = (type: 'check-in' | 'therapy' | 'initial-consultation') => {
    if (allowedAppointmentTypes.includes(type)) {
      setAllowedAppointmentTypes(allowedAppointmentTypes.filter(t => t !== type));
    } else {
      setAllowedAppointmentTypes([...allowedAppointmentTypes, type]);
    }
  };

  if (!isOpen) return null;

  const dateStr = selectedDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="availability-modal-title"
        aria-describedby="availability-modal-description"
        style={{
          backgroundColor: '#1f2937',
          borderRadius: '8px',
          padding: '24px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
      >
        <h2 id="availability-modal-title" style={{ color: '#f9fafb', marginBottom: '8px', fontSize: '1.5rem' }}>
          Override Availability
        </h2>
        <p id="availability-modal-description" style={{ color: '#d1d5db', marginBottom: '24px' }}>
          {dateStr}
        </p>

        {/* Availability Toggle */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
            color: '#f9fafb'
          }}>
            <input
              type="checkbox"
              checked={isAvailable}
              onChange={(e) => setIsAvailable(e.target.checked)}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer'
              }}
            />
            <span>I am available on this date</span>
          </label>
        </div>

        {/* Show time settings only if available */}
        {isAvailable && (
          <>
            {/* Time Range */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#f9fafb', marginBottom: '8px' }}>
                Available Hours (whole hours only)
              </label>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <select
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '4px',
                    color: '#f9fafb'
                  }}
                >
                  {Array.from({ length: 16 }, (_, i) => i + 6).map(hour => {
                    const time = `${hour.toString().padStart(2, '0')}:00`;
                    const displayTime = hour >= 12 ? `${hour === 12 ? 12 : hour - 12}:00 PM` : `${hour}:00 AM`;
                    return <option key={time} value={time}>{displayTime}</option>;
                  })}
                </select>
                <span style={{ color: '#d1d5db' }}>to</span>
                <select
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '4px',
                    color: '#f9fafb'
                  }}
                >
                  {Array.from({ length: 16 }, (_, i) => i + 6).map(hour => {
                    const time = `${hour.toString().padStart(2, '0')}:00`;
                    const displayTime = hour >= 12 ? `${hour === 12 ? 12 : hour - 12}:00 PM` : `${hour}:00 AM`;
                    return <option key={time} value={time}>{displayTime}</option>;
                  })}
                </select>
              </div>
            </div>

            {/* Appointment Types Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#f9fafb', marginBottom: '8px', fontWeight: 'bold' }}>
                Allowed Appointment Types
              </label>
              <p style={{ color: '#d1d5db', fontSize: '13px', marginBottom: '12px' }}>
                Select which appointment types clients can book during these hours:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  color: '#f9fafb',
                  padding: '8px',
                  backgroundColor: '#374151',
                  borderRadius: '4px'
                }}>
                  <input
                    type="checkbox"
                    checked={allowedAppointmentTypes.includes('check-in')}
                    onChange={() => toggleAppointmentType('check-in')}
                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                  />
                  <span>Check-in / Follow-up <span style={{ color: '#d1d5db' }}>(30 min)</span></span>
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  color: '#f9fafb',
                  padding: '8px',
                  backgroundColor: '#374151',
                  borderRadius: '4px'
                }}>
                  <input
                    type="checkbox"
                    checked={allowedAppointmentTypes.includes('therapy')}
                    onChange={() => toggleAppointmentType('therapy')}
                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                  />
                  <span>Regular Therapy Session <span style={{ color: '#d1d5db' }}>(50 min)</span></span>
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  color: '#f9fafb',
                  padding: '8px',
                  backgroundColor: '#374151',
                  borderRadius: '4px'
                }}>
                  <input
                    type="checkbox"
                    checked={allowedAppointmentTypes.includes('initial-consultation')}
                    onChange={() => toggleAppointmentType('initial-consultation')}
                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                  />
                  <span>Initial Consultation <span style={{ color: '#d1d5db' }}>(60 min)</span></span>
                </label>
              </div>
            </div>

            {/* Modalities */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#f9fafb', marginBottom: '8px' }}>
                Appointment Types
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  color: '#f9fafb'
                }}>
                  <input
                    type="checkbox"
                    checked={modalities.includes('video')}
                    onChange={() => toggleModality('video')}
                    style={{ cursor: 'pointer' }}
                  />
                  Video
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  color: '#f9fafb'
                }}>
                  <input
                    type="checkbox"
                    checked={modalities.includes('in-person')}
                    onChange={() => toggleModality('in-person')}
                    style={{ cursor: 'pointer' }}
                  />
                  In-Person
                </label>
              </div>
            </div>
          </>
        )}

        {/* Reason */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', color: '#f9fafb', marginBottom: '8px' }}>
            Reason {!isAvailable && <span style={{ color: '#ef4444' }}>*</span>}
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={isAvailable ? "e.g., Half day, Conference" : "e.g., Doctor's appointment, Vacation"}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: '#374151',
              border: '1px solid #4b5563',
              borderRadius: '4px',
              color: '#f9fafb'
            }}
          />
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleDelete}
            style={{
              padding: '8px 16px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
          >
            Delete Override
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#374151',
              color: '#f9fafb',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#374151'}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isAvailable && !reason}
            style={{
              padding: '8px 16px',
              backgroundColor: (!isAvailable && !reason) ? '#6b7280' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (!isAvailable && !reason) ? 'not-allowed' : 'pointer',
              fontWeight: '500'
            }}
            onMouseOver={(e) => {
              if (isAvailable || reason) {
                e.currentTarget.style.backgroundColor = '#2563eb';
              }
            }}
            onMouseOut={(e) => {
              if (isAvailable || reason) {
                e.currentTarget.style.backgroundColor = '#3b82f6';
              }
            }}
          >
            Save Override
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateSpecificAvailabilityModal;
